import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { loaderDecrement, loaderIncrement } from "../services/globalLoader";
import {
  isOwnerAwaitingPropertyApproval,
  normalizePropertyInfo,
  PROPERTY_INFO_CHANGED_EVENT,
  readPropertyInfo,
  writePropertyInfo
} from "../utils/propertyAccess";

const pickProperty = (data) => {
  if (!data || typeof data !== "object") return null;
  if (data.property && typeof data.property === "object") return data.property;
  if (data.tenant && typeof data.tenant === "object") return data.tenant;
  if (data.result && typeof data.result === "object" && !Array.isArray(data.result)) return data.result;
  if (data.data && typeof data.data === "object" && !Array.isArray(data.data)) return data.data;
  if (data.name || data.email || data.flatNumber || data.id || data._id) {
    const { status, message, success, ...rest } = data;
    return Object.keys(rest).length ? rest : data;
  }
  return null;
};

const formatInr = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0
  }).format(Number(n || 0));

const formatMoney = (value) => {
  if (value == null || value === "") return null;
  const num = Number(value);
  if (Number.isNaN(num)) return null;
  return formatInr(num);
};

const formatDate = (iso) => {
  if (iso == null || iso === "") return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString("en-IN", { dateStyle: "medium" });
};

const relationshipLabel = (value) => {
  const map = {
    spouse: "Spouse",
    son: "Son",
    daughter: "Daughter",
    brother: "Brother",
    sister: "Sister",
    father: "Father",
    mother: "Mother"
  };
  const key = String(value || "").trim().toLowerCase();
  return map[key] || value;
};

const DetailRow = ({ label, value }) => (
  <div className="row py-2 border-bottom" style={{ margin: 0 }}>
    <div className="col-12 col-sm-4 text-muted" style={{ fontSize: 13, fontWeight: 600 }}>
      {label}
    </div>
    <div className="col-12 col-sm-8" style={{ fontSize: 14 }}>
      {value != null && value !== "" ? String(value) : "—"}
    </div>
  </div>
);

const SectionTitle = ({ children, first }) => (
  <div
    className="text-muted text-uppercase"
    style={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.06em",
      marginTop: first ? 0 : 18,
      marginBottom: 8
    }}
  >
    {children}
  </div>
);

const PropertyDetails = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const userid = useMemo(() => {
    try {
      return String(localStorage.getItem("userid") || "").trim();
    } catch {
      return "";
    }
  }, []);

  const [loading, setLoading] = useState(true);
  const [property, setProperty] = useState(null);

  const fetchProperty = useCallback(async () => {
    if (!API_BASE_URL) {
      toast.error("Missing REACT_APP_API_URL in environment");
      setLoading(false);
      return;
    }
    if (!userid) {
      toast.error("Missing userid. Please login again.");
      setLoading(false);
      return;
    }

    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;
      const response = await axios.post(`${API_BASE_URL}`, {
        action: "owner.getProperty",
        userid
      });
      const data = response?.data;
      if (data?.success === true || data?.status === "success") {
        const loaded = pickProperty(data);
        setProperty(loaded);
        const info =
          data?.propertyInfo ||
          data?.data?.propertyInfo ||
          loaded?.propertyInfo;
        if (normalizePropertyInfo(info) === "approved") {
          writePropertyInfo("Approved");
        }
      } else {
        toast.error(data?.message || "Failed to load property details");
        setProperty(null);
      }
    } catch (error) {
      toast.error(error?.response?.data?.message || error?.message || "Something went wrong");
      setProperty(null);
    } finally {
      if (didIncrement) loaderDecrement();
      setLoading(false);
    }
  }, [API_BASE_URL, userid]);

  useEffect(() => {
    fetchProperty();
  }, [fetchProperty]);

  const isJointOwner = property?.propertyType === "Joint Owner";
  const isLetOut = property?.propertyNature === "Let-out";
  const isForLease = property?.typeOfTenancy === "For Lease";
  const [propertyInfo, setPropertyInfo] = useState(() => readPropertyInfo());
  const awaitingApproval = isOwnerAwaitingPropertyApproval(
    localStorage.getItem("role"),
    propertyInfo
  );

  useEffect(() => {
    const sync = () => setPropertyInfo(readPropertyInfo());
    window.addEventListener("storage", sync);
    window.addEventListener(PROPERTY_INFO_CHANGED_EVENT, sync);
    return () => {
      window.removeEventListener("storage", sync);
      window.removeEventListener(PROPERTY_INFO_CHANGED_EVENT, sync);
    };
  }, []);

  return (
    <div className="employee-form">
      <div className="content-header">
        <div className="container-fluid d-flex justify-content-between align-items-end flex-wrap" style={{ gap: 12 }}>
          <div>
            <h1 className="m-0 txt_lft">Property details</h1>
            <p style={{ margin: 0, color: "#6c757d", fontSize: 14 }}>
              Your property and occupancy details
            </p>
          </div>
          {!loading ? (
            <Link
              to={
                property
                  ? `/property-details-edit/${encodeURIComponent(String(property.id || property._id || userid))}`
                  : "/property-details-add"
              }
              className="btn btn-primary"
            >
              {property ? "Edit" : "Add"}
            </Link>
          ) : null}
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          {awaitingApproval ? (
            <div
              className="alert"
              role="status"
              style={{
                background: "#e7f6f8",
                border: "1px solid #b7e0e6",
                color: "#0d5c66"
              }}
            >
              <div style={{ fontWeight: 700, marginBottom: 4 }}>
                Status: {propertyInfo || "waitingforApproval"}
              </div>
              <div>Once approved, you will be able to access the site.</div>
            </div>
          ) : null}
          {!loading && !property ? (
            <div className="card">
              <div className="card-body text-muted">No property details found</div>
            </div>
          ) : property ? (
            <div className="property-page-grid">
              <div className="card property-page-panel">
                <div className="card-body">
                  <SectionTitle first>Primary owner</SectionTitle>
                  <div className="border rounded overflow-hidden">
                    <DetailRow label="Primary owner name" value={property.name} />
                    <DetailRow label="Email" value={property.email} />
                    <DetailRow label="Primary owner's mobile no." value={property.phone} />
                    <DetailRow
                      label="Primary owner's alternative mobile no."
                      value={property.primaryOwnerAlternativeMobile}
                    />
                    <DetailRow label="Primary owner's WhatsApp no." value={property.primaryOwnerWhatsapp} />
                  </div>
                </div>
              </div>

              <div className="card property-page-panel">
                <div className="card-body">
                  <SectionTitle first>Property</SectionTitle>
                  <div className="border rounded overflow-hidden">
                    <DetailRow label="Block" value={property.block} />
                    <DetailRow label="Flat number" value={property.flatNumber} />
                    <DetailRow label="BHK type" value={property.bhkType} />
                    <DetailRow label="Sq.ft (built up area)" value={property.squareFeet} />
                    <DetailRow label="Property purchase date" value={formatDate(property.dateOfPropertyPurchase)} />
                    <DetailRow label="Date of occupancy" value={formatDate(property.dateOfOccupancy)} />
                    <DetailRow label="Property type" value={property.propertyType} />
                    <DetailRow label="Property nature" value={property.propertyNature} />
                  </div>
                </div>
              </div>

              <div className="card property-page-panel">
                <div className="card-body">
                  <SectionTitle first>Corpus and maintenance</SectionTitle>
                  <div className="border rounded overflow-hidden">
                    <DetailRow label="Corpus fund amount" value={formatMoney(property.corpusFundAmount)} />
                    <DetailRow label="Corpus fund paid date" value={formatDate(property.corpusFundPaidDate)} />
                    <DetailRow
                      label="Monthly maintenance amount"
                      value={formatMoney(property.monthlyMaintenanceAmount)}
                    />
                  </div>
                </div>
              </div>

              {isJointOwner ? (
                <div className="card property-page-panel">
                  <div className="card-body">
                    <SectionTitle first>Joint owner</SectionTitle>
                    <div className="border rounded overflow-hidden">
                      <DetailRow label="Joint owner name" value={property.jointOwnerName} />
                      <DetailRow
                        label="Relationship with primary owner"
                        value={relationshipLabel(property.jointOwnerRelationship)}
                      />
                      <DetailRow label="Joint owner's email" value={property.jointOwnerEmail} />
                      <DetailRow label="Joint owner's mobile no." value={property.jointOwnerMobile} />
                      <DetailRow label="Joint owner's WhatsApp no." value={property.jointOwnerWhatsapp} />
                    </div>
                  </div>
                </div>
              ) : null}

              {isLetOut ? (
                <div className={`card property-page-panel${isJointOwner ? " property-page-panel-wide" : ""}`}>
                  <div className="card-body">
                    <SectionTitle first>Tenant details</SectionTitle>
                    <div className="border rounded overflow-hidden">
                      <DetailRow label="Let out portion" value={property.letOutPortion} />
                      <DetailRow label="Type of tenancy" value={property.typeOfTenancy} />
                      {isForLease ? <DetailRow label="Lease period" value={property.leasePeriod} /> : null}
                      <DetailRow label="Tenant name" value={property.tenantName} />
                      <DetailRow label="Tenant Email" value={property.tenantEmail} />
                      <DetailRow label="Tenant Phone Number" value={property.tenantPhoneNumber} />
                      <DetailRow label="Tenancy starting date" value={formatDate(property.tenancyStartingDate)} />
                      <DetailRow label="Advance amount/lease amount" value={formatMoney(property.advanceAmount)} />
                      <DetailRow label="Monthly rent" value={formatMoney(property.monthlyRent)} />
                      <DetailRow
                        label="Monthly maintenance charges"
                        value={property.monthlyMaintenanceChargesBy}
                      />
                    </div>
                  </div>
                </div>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default PropertyDetails;